import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacing['3xl']};
  padding: 180px ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing['3xl']};
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0f9ff 100%);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: radial-gradient(circle at 50% 50%, rgba(0, 102, 204, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    padding: 160px ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing['2xl']};
    text-align: center;
  }
`;

const ContentSide = styled.div`
  position: relative;
  z-index: 10;
  max-width: 600px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
    margin: 0 auto;
  }
`;

const VisualSide = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 600px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 400px;
    order: -1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const CircleDecoration = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 2px solid ${({ $color }) => $color};
  opacity: 0.2;
  animation: ${rotate} ${({ $duration }) => $duration}s linear infinite;
`;

const Circle1 = styled(CircleDecoration)`
  width: 400px;
  height: 400px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 250px;
    height: 250px;
  }
`;

const Circle2 = styled(CircleDecoration)`
  width: 500px;
  height: 500px;
  animation-direction: reverse;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 320px;
    height: 320px;
  }
`;

const Circle3 = styled(CircleDecoration)`
  width: 300px;
  height: 300px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 180px;
    height: 180px;
  }
`;

const CentralCard = styled.div`
  position: relative;
  z-index: 5;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 102, 204, 0.15);
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  padding: ${({ theme }) => theme.spacing['2xl']};
  box-shadow: 0 20px 60px rgba(0, 102, 204, 0.15);
  animation: ${float} 6s ease-in-out infinite;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: #64748b;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const PreTitle = styled.div`
  display: inline-block;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: rgba(0, 102, 204, 0.15);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid rgba(0, 102, 204, 0.4);
  animation: ${scaleIn} 0.8s ease-out;
`;

const Title = styled.h1`
  color: #1e293b;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  line-height: 1.2;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  animation: ${fadeInUp} 1s ease-out 0.2s backwards;
  
  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  animation: ${fadeInUp} 1s ease-out 0.6s backwards;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: 0 10px 30px rgba(0, 102, 204, 0.3);
  transition: all ${({ theme }) => theme.transitions.base};
  border: none;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(0, 102, 204, 0.5);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #1e293b;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 2px solid #cbd5e1;
  transition: all ${({ theme }) => theme.transitions.base};
  cursor: pointer;
  
  &:hover {
    background: #f1f5f9;
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-3px);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  animation: ${slideIn} 1s ease-out 0.8s backwards;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  color: #475569;
  font-size: ${({ theme }) => theme.fontSizes.base};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  &::before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
    border-radius: 50%;
    color: white;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const PillContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeInUp} 1s ease-out 1s backwards;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const GlassPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  box-shadow: 
    0 8px 32px rgba(0, 102, 204, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  color: #1e293b;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  transition: all ${({ theme }) => theme.transitions.base};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 12px 40px rgba(0, 102, 204, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: rgba(0, 102, 204, 0.3);
    
    &::before {
      left: 100%;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.xs};
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  }
`;

const PillIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  border-radius: 50%;
  color: white;
  font-size: 9px;
  font-weight: bold;
  flex-shrink: 0;
`;

export const Hero = ({ onCTA }) => {
  return (
    <HeroSection id="home">
      <ContentSide>
        <PreTitle>Revenue Cycle Excellence</PreTitle>
        
        <Title>
          Transform Your Healthcare <span>Financial Operations</span>
        </Title>
        
        <ButtonGroup>
          <PrimaryButton onClick={onCTA} aria-label="Get started with consultation">
            Get Started →
          </PrimaryButton>
          <SecondaryButton onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>
            Our Services
          </SecondaryButton>
        </ButtonGroup>
        
        <FeatureList>
          <FeatureItem>99% First-Pass Claims Acceptance Rate</FeatureItem>
          <FeatureItem>30% Reduction in Days in A/R</FeatureItem>
          <FeatureItem>24/7 Dedicated Support Team</FeatureItem>
        </FeatureList>
        
        <PillContainer>
          <GlassPill>
            <PillIcon>✓</PillIcon>
            Claims Management
          </GlassPill>
          <GlassPill>
            <PillIcon>✓</PillIcon>
            Medical Coding
          </GlassPill>
          <GlassPill>
            <PillIcon>✓</PillIcon>
            Medical Billing Services
          </GlassPill>
        </PillContainer>
      </ContentSide>
      
      <VisualSide>
        <Circle1 $color="#0066CC" $duration={20} />
        <Circle2 $color="#00A8A8" $duration={25} />
        <Circle3 $color="#0088DD" $duration={30} />
        
        <CentralCard>
          <StatGrid>
            <StatItem>
              <StatNumber>200+</StatNumber>
              <StatLabel>Clients Served</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>$50M+</StatNumber>
              <StatLabel>Revenue Recovered</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>99%</StatNumber>
              <StatLabel>Clean Claims Rate</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>15+</StatNumber>
              <StatLabel>Years Experience</StatLabel>
            </StatItem>
          </StatGrid>
        </CentralCard>
      </VisualSide>
    </HeroSection>
  );
};
